/**
 * Lag compensation. A player sees remote fighters in the past (network delay
 * plus the interpolation buffer), so a strike is resolved against where
 * opponents *stood and faced* at the tick the attacker was looking at.
 *
 * Only position and facing move back. Guard, check, stance, parry, crouch and slip are the
 * defender's actions and are judged as the server has them now: rewinding
 * them would let a laggy attacker break a guard the defender raised in time
 * on their own screen.
 */
import type { PoseSource } from "../skeleton.ts";
import { type FighterState, type MatchState, PAST_POSES } from "../state.ts";
import { tuning } from "../tuning.ts";

/**
 * Ticks to rewind opponents for an input made while looking at `viewTick`,
 * during the step that produces tick `state.tick + 1`. 0 means the present.
 */
export function rewindTicks(state: MatchState, viewTick: number): number {
  if (viewTick <= 0) return 0;
  const r = state.tick + 1 - viewTick;
  const max = tuning.lagCompensation.maxRewindTicks;
  return r < 0 ? 0 : r > max ? max : r;
}

/** Stores every fighter's end-of-tick position. Call after the tick advanced. */
export function recordPast(state: MatchState): void {
  const slot = state.tick % PAST_POSES;
  for (const f of state.fighters) {
    const p = f.past[slot];
    if (!p) continue;
    p.tick = state.tick;
    p.x = f.pos.x;
    p.z = f.pos.z;
    p.facing = f.facing;
  }
}

/**
 * The pose an attacker saw `rewind` ticks ago: the target's current limbs,
 * guard, crouch and slip on the position and facing it had then. The present
 * when `rewind` is 0 or that tick is no longer (or not yet) in the ring.
 */
export function poseSeen(state: MatchState, target: FighterState, rewind: number): PoseSource {
  if (rewind <= 0) return target;
  const tick = state.tick + 1 - rewind;
  const p = target.past[tick % PAST_POSES];
  if (!p || p.tick !== tick) return target;
  return {
    pos: { x: p.x, y: target.pos.y, z: p.z },
    facing: p.facing,
    duck: target.duck,
    lean: target.lean,
    stance: target.stance,
    twist: target.twist,
    guard: target.guard,
    handL: target.handL,
    handR: target.handR,
    footL: target.footL,
    footR: target.footR,
  };
}
